import React from 'react';
import { Box, Grid } from '@mui/material';
import { FallbackSkeleton } from 'ui/skeleton/FallbackSkeleton';
import { SkeletonProps } from 'ui/skeleton/SkeletonComponent';
import { Notification } from 'components/notification/models/Notification';
import { RuleResource } from 'components/rules/rules.types';
import { NotificationReceiptsCard } from 'components/notification/cards/NotificationReceiptsCard';
import { NotificationResultsCard } from 'components/notification/cards/NotificationResultsCard';
import { RuleScheduledTaskNotificationDetailsCard } from 'components/rules/scheduled-notification/cards/RuleScheduledTaskNotificationDetailsCard';

export interface RuleScheduledTaskNotificationViewProps {
    rule: RuleResource;
    notifications: Notification<string>[];
    metrics: { sent: number; skipped: number; failures: number };
    loading: boolean;
}

const fallbackProps: Partial<SkeletonProps> = {
    height: '320px'
};

export const RuleScheduledTaskNotificationView: React.FC<RuleScheduledTaskNotificationViewProps> = ({
    rule,
    notifications,
    metrics,
    loading
}) => {
    const isLoading = React.useMemo(() => loading || !rule?._id, [loading, rule]);
    const isUnsent = React.useMemo(() => !rule?.active, [rule]);
    return (
        <>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            md: 6
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <RuleScheduledTaskNotificationDetailsCard rule={rule} />
                                </FallbackSkeleton>
                            </Grid>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <NotificationResultsCard
                                        isLoading={isLoading}
                                        isOngoing
                                        metrics={metrics}
                                    />
                                </FallbackSkeleton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        size={{
                            md: 6
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <NotificationReceiptsCard
                                        notifications={notifications}
                                        extraFilters={[{ key: 'ruleId', value: rule?._id }]}
                                        isLoading={isLoading}
                                        isUnsent={isUnsent}
                                        showNotificationIdFilter
                                    />
                                </FallbackSkeleton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block', lg: 'none' } }}>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <RuleScheduledTaskNotificationDetailsCard rule={rule} />
                                </FallbackSkeleton>
                            </Grid>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <NotificationResultsCard
                                        isLoading={isLoading}
                                        isOngoing
                                        metrics={metrics}
                                    />
                                </FallbackSkeleton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        size={{
                            md: 6
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FallbackSkeleton loading={loading} {...fallbackProps}>
                                    <NotificationReceiptsCard
                                        notifications={notifications}
                                        extraFilters={[{ key: 'ruleId', value: rule?._id }]}
                                        isLoading={isLoading}
                                        isUnsent={isUnsent}
                                        showNotificationIdFilter
                                    />
                                </FallbackSkeleton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <FallbackSkeleton loading={loading} {...fallbackProps}>
                            <RuleScheduledTaskNotificationDetailsCard rule={rule} />
                        </FallbackSkeleton>
                    </Grid>
                    <Grid size={12}>
                        <FallbackSkeleton loading={loading} {...fallbackProps}>
                            <NotificationResultsCard isLoading={isLoading} isOngoing metrics={metrics} />
                        </FallbackSkeleton>
                    </Grid>
                    <Grid size={12}>
                        <FallbackSkeleton loading={loading} {...fallbackProps}>
                            <NotificationReceiptsCard
                                notifications={notifications}
                                extraFilters={[{ key: 'ruleId', value: rule?._id }]}
                                isLoading={isLoading}
                                isUnsent={isUnsent}
                                showNotificationIdFilter
                            />
                        </FallbackSkeleton>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
